/* eslint-disable no-param-reassign */
import { ActionReducerMapBuilder, createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { brandColors } from '../../config/branding';
import DefaultApiLimit from '../../config/defaultLimit';
import { ChargingStationListInterface } from '../../interface/facility-tag.interface';
import {
  DeviceListInterface,
  RequestVlbcsInterface,
  VlbcInitialProperties, 
} from '../../interface/vlbc.interface';
import vlbcApi from '../../utils/api/vlbc';

export const fetchVlbcs = createAsyncThunk(
  'vlbc/fetchVlbcs',
  async (data: RequestVlbcsInterface, { dispatch }) => {
    try {
      const response = await vlbcApi.fetchVlbcs(data);
      const convertedResult = response.data.results.map((objectResponse: any) => {
        const dataTemporary = [
          {
            label: 'Capacity',
            labelText: `Capacity(${objectResponse.maximumOutputPower ?? 0})`,
            value: Number(objectResponse?.maximumOutputPower ?? 0),
            fill: '#00A79D1A',
          },
          {
            label: 'Used',
            labelText: `Used(${objectResponse?.totalPowerInUse ?? 0})`,
            value: Number(objectResponse?.totalPowerInUse ?? 0),
            fill: '#00A79D1A',
          },
        ];
        const highestValue = Math.max(...dataTemporary.map((item) => item.value || 0)); // Default to 0 if undefined

        const processedData = dataTemporary.map((item) => ({
          ...item,
          fill:
            item.value === highestValue && item.label === 'Used'
              ? `url(#barGradient)`
              : brandColors.reportChart1,
        }));

        return {
          ...objectResponse,
          processedData,
        };
      });
      return {
        ...response.data,
        results: convertedResult,
      };
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationDetails',
        payload: {
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || 'Something went wrong!',
          },
        },
      });
      throw error;
    }
  },
);

export const fetchDevices = createAsyncThunk(
  'vlbc/fetchDevices',
  async (chargingStationId: string, { dispatch }) => {
    try {
      const response = await vlbcApi.fetchDevices(chargingStationId);
      return {
        results: response.data.map((devices: DeviceListInterface) => ({
          ...devices,
          id: devices._id,
        })),
      };
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationDetails',
        payload: {
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || 'Something went wrong!',
          },
        },
      });
      throw error;
    }
  },
);

export const fetchChargingStations = createAsyncThunk(
  'vlbc/fetchChargingStations',
  async (_, { dispatch }) => {
    try {
      const response = await vlbcApi.fetchChargingStations();
      return response.data.map((chargingStation: ChargingStationListInterface) => ({
        value: chargingStation._id,
        label: chargingStation.name,
      }));
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationDetails',
        payload: {
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || 'Something went wrong!',
          },
        },
      });
      throw error;
    }
  },
);

const initialState: VlbcInitialProperties = {
  devices: {
    data: [],
    loading: false,
  },
  vlbcs: {
    data: [],
    loading: false,
    page: DefaultApiLimit.page,
    limit: DefaultApiLimit.limit,
    totalPages: 1,
    totalResults: 0,
    filter: {},
    searchBy: '',

    
  },
  chargingStations: {
    data: [],
  },
};

// Create the slice
export const vlbcSlice = createSlice({
  name: 'vlbc',
  initialState,
  reducers: {

      /**
       * setVlbcSearchBy function to update the searchBy property in vlbcs state
       * @param state
       * @param action
       */
        setVlbcSearchBy: (state, action) => {
        state.vlbcs.searchBy = action.payload;
       },
    /**
     * updateDevices function to update the devices state
     * @param state
     * @param action
     */
    updateDevices: (state, action) => {
      console.log(action.payload);
      state.devices.data = action.payload;
    },
    /**
     * updateVlbcStatus function to update the vlbc status
     * @param state
     * @param action
     */
    updateVlbcStatusRedux: (state, action) => {
      const { vlbcId, isActive } = action.payload;

      // Update only the matching item
      state.vlbcs.data = state.vlbcs.data.map((item) =>
        item._id.toString() === vlbcId.toString() ? { ...item, isActive } : item,
      );
    },

    /**
     * removeVlbc function to update the vlbc status
     * @param state
     * @param action
     */
    removeVlbc: (state, action) => {
      const { vlbcId } = action.payload;

      // Remove the item with matching _id
      state.vlbcs.data = state.vlbcs.data.filter(
        (item) => item._id.toString() !== vlbcId.toString(),
      );
    },
  },
  /**
   * Extra reducers to handle thunk request
   * @param builder
   */
  extraReducers: (builder: ActionReducerMapBuilder<VlbcInitialProperties>) => {
    builder
      .addCase(fetchDevices.pending, (state) => {
        state.devices.loading = true;
      })
      .addCase(fetchDevices.fulfilled, (state, action) => {
        state.devices.loading = false;
        state.devices.data = action.payload.results;
      })
      .addCase(fetchDevices.rejected, (state) => {
        state.devices.loading = false;
      });

    builder.addCase(fetchChargingStations.fulfilled, (state, action) => {
      state.chargingStations.data = action.payload;
    });

    builder
      .addCase(fetchVlbcs.pending, (state) => {
        state.vlbcs.loading = true;
      })
      .addCase(fetchVlbcs.fulfilled, (state, action) => {
        state.vlbcs.loading = false;
        state.vlbcs.page = action.payload.page;
        state.vlbcs.limit = action.payload.limit;
        state.vlbcs.totalPages = action.payload.totalPages;
        state.vlbcs.totalResults = action.payload.totalResults;
        state.vlbcs.data = action.payload.results;
      })
      .addCase(fetchVlbcs.rejected, (state) => {
        state.vlbcs.loading = false;
      });
  },
});

// Export the actions
export const { updateDevices, updateVlbcStatusRedux, removeVlbc,setVlbcSearchBy } = vlbcSlice.actions;

// Export the reducer
export default vlbcSlice.reducer;
